import React, { useEffect, useRef, useState } from 'react';
import { useInstantSearch } from 'react-instantsearch';
import useSessionsDetailModal from '../../hooks/useSessionsDetailModal';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import getFavoriteHook from '@hooks/getFavoriteHook';
/**
 * SessionsUrlAutoOpener
 * Listens to Algolia results. If a session slug exists in the URL, it finds
 * that session in the result set and triggers the detail modal automatically.
 */
const SessionsUrlAutoOpener = ({ slug, mapData }) => {
    const { results } = useInstantSearch();
    const [targetHit, setTargetHit] = useState(null);
    const hasOpenedRef = useRef(false);

    useEffect(() => {
        if (slug && results?.hits?.length > 0 && !hasOpenedRef.current) {
            const hit = results.hits.find(h => h.slug === slug);
            if (hit) {
                // Store the raw hit first so ModalTrigger can initialize the favorite hook
                setTargetHit(hit);
            }
        }
    }, [slug, results]);

    if (!targetHit) return null;

    return (
        <ModalTrigger
            hit={targetHit}
            slug={slug}
            mapData={mapData}
            onComplete={() => {
                hasOpenedRef.current = true;
                setTargetHit(null);
            }}
        />
    );
};

/**
 * ModalTrigger
 * Handles the actual interaction with the Modal hook and URL sync on close.
 */
const ModalTrigger = ({ hit, slug, mapData, onComplete }) => {
    const history = useHistory();
    const location = useLocation();
    const hasTriggeredRef = useRef(false);

    // Map the Algolia data
    const sessionProps = mapData(hit);

    // Initialize Favorite logic specifically for this session
    const useFavorite = getFavoriteHook({
        type: 'session',
        initialIsFavorite: sessionProps.isFavorite
    });
    const { isFavorite, isLoadingFavorite, saveFavorite, removeFavorite } = useFavorite();

    const { openModal } = useSessionsDetailModal({
        ...sessionProps,
        isFavorite,
        isLoadingFavorite,
        toggleFavorite: (e) => {
            if (e && e.stopPropagation) e.stopPropagation();
            isFavorite ? removeFavorite(sessionProps.id) : saveFavorite(sessionProps.id);
        },
        onClose: () => {
            const currentPath = history.location.pathname.replace(/\/$/, '');
            const segments = currentPath.split('/');
            const lastSegment = segments[segments.length - 1];

            // If the URL still contains the slug, pop it off to return to the list
            if (slug && lastSegment === slug) {
                segments.pop();
                const newPath = segments.join('/');
                history.push({
                    pathname: newPath,
                    search: location.search,
                });
            }
        },
    });

    useEffect(() => {
        if (!hasTriggeredRef.current) {
            openModal();
            hasTriggeredRef.current = true;
            if (onComplete) onComplete();
        }
    }, [openModal, onComplete]);

    return null;
};

export default SessionsUrlAutoOpener;
